import { useParams } from "react-router-dom";
import clsx from "clsx";
import { LoadingIndicator, SideNavGroup } from "@k8slens/lds";
import { type Business, ForbiddenException, type UserWithEmail } from "lens-platform-sdk";

import { useBillingPageUrl, useBusinessBillingPageUrl } from "src/hooks/useBillingPageUrl";
import { useGetLicense } from "src/hooks/useGetLicense";
import { features } from "src/utils/license";
import { useGetBusinessUsers } from "src/hooks/useGetBusinessUsers";

import MainNavigation from "src/components/MainNavigation/MainNavigation";
import MainNavLink from "src/components/MainNavigation/MainNavigationItem";

import styles from "./Layout.module.css";

interface Props {
  className?: string;
  children: React.ReactNode, logout: () => void;
  profile: UserWithEmail | null;
  business?: Business
}

export default function Layout({
  className, children, logout, profile, business
}: Props) {
  const { businessId } = useParams<{ businessId?: string }>();
  const isExternal = business?.external === true;

  // No need to fetch urls for user if we have businessId
  const billingPageUser = businessId ? undefined : profile?.username;

  const { accountUrl: userAccountUrl } = useBillingPageUrl(billingPageUser);
  const { accountUrl: businessAccountUrl } = useBusinessBillingPageUrl(businessId);
  const { error: errorGetBusinessUser } = useGetBusinessUsers(businessId);

  // "Member" has not permission to get business users
  const isBusinessMember = errorGetBusinessUser instanceof ForbiddenException;
  const accountUrl = businessAccountUrl || userAccountUrl;
  const license = useGetLicense();

  if (!profile) {
    return (
      <div className={clsx("lds-web", styles.layout, className)}>
        <LoadingIndicator size="xxl" />
      </div>
    );
  }

  const isSupportEnabled = license?.features?.includes?.(features.support);

  return (
    <div className={clsx("lds-web", styles.layout, className)}>
      <MainNavigation profile={profile} logout={logout} className={styles.sidebar}>
        <SideNavGroup aria-label="Navigation" className={styles.homeLink}>
          <MainNavLink to={businessId ? `/business/${businessId}` : "/home"} title="Home" exact />
        </SideNavGroup>
        <SideNavGroup label="Settings">
          <MainNavLink to={businessId ? `/business/${businessId}/profile` : "/profile"} title="Profile" />
          {(businessId && !isBusinessMember) && (
            <MainNavLink
              to={`/business/${businessId}/account`}
              title="Account"
            />
          )}
          {!businessId && (
          <MainNavLink
            to="/account"
            title="Account"
          />
          )}
          {businessId ? (
            <MainNavLink
              to={`/business/${businessId}/billing-details`}
              title="Billing Details"
              disabled={!accountUrl || isExternal}
            />
          ) : (
            <MainNavLink
              to="/billing-details"
              title="Billing Details"
              disabled={!accountUrl || isExternal}
            />
          )}
          {businessId ? null : <MainNavLink to="/emails" title="Emails" />}
          {businessId ? null : <MainNavLink to="/support" title="Support" disabled={!isSupportEnabled} />}
          {businessId ? <MainNavLink to={`/business/${businessId}/subscriptions`} title="Manage Seats" /> : null}
        </SideNavGroup>
      </MainNavigation>
      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
}
